/**
 * AULA DEL ALUMNO (A1) — la portada de UNA cohorte vista por quien la cursa.
 *
 * Es el hueco más grande del análisis de brecha: el alumno no tenía casa.
 * `/academy/cohorts` es del escalón docente, así que quien estudia no tenía
 * ninguna pantalla de su aula aunque el dato llevara tiempo en su dispositivo.
 *
 * Esto es ENSAMBLAJE, no dominio nuevo: cabecera (curso, profesorado público,
 * próxima clase), avance, anuncios de la cohorte y las puertas a los módulos
 * del curso y al plan de cada clase. Todo lo que lee ya viaja por
 * `my_cohorts_student`, así que el aula funciona sin conexión y no hace falta
 * ninguna regla de sync nueva.
 *
 * B2 añade dos bloques, cada uno tras SU flag y ambos apagables sin tocar el
 * resto del aula:
 *   · COMPAÑEROS (`academy.classroom.roster`) — nombre y avatar, nada más. La
 *     decisión de privacidad (A3/Q2) ya está tomada y vive en el servidor:
 *     `GET /api/cohorts/:id/roster` sirve al compañero una vista con el correo
 *     y la sede redactados, así que la pantalla no puede enseñar de más.
 *   · CHAT (`academy.classroom.chat`) — conversación de la cohorte, con
 *     moderación efectiva y ventana de retención. Convive con el foro: si el
 *     chat hay que apagarlo por coste, el aula NO se queda muda.
 */
import { type Component, createMemo, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import {
  BookOpen,
  CalendarClock,
  ClipboardList,
  ListChecks,
  Megaphone,
  Pin,
  School,
  Video,
} from "lucide-solid";
import { joinWindowState, localDateKey } from "@space/shared";
import { Link } from "@/lib/u-router";
import { useI18n } from "@/providers/i18n-provider";
import { PageContainer } from "@/components/ui/page-container";
import { Skeleton } from "@/components/ui/skeleton";
import { NotFoundState } from "@/components/ui/not-found-state";
import { HelpButton } from "@/features/help/components/help-button";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";
import { EnVivoBadge } from "@/features/live/components/en-vivo-badge";
import { RecordingPlayer } from "@/features/live/components/recording-player";
import { useNow } from "@/features/live/hooks/use-now";
import { useCourseTree } from "@/features/courses/hooks/use-course-data";
import { CompletionProgress } from "../../completion/completion-progress";
import { useCohort, useCohortTeacherProfiles } from "../../hooks/use-cohort-data";
import { useAnnouncementsByCohort } from "../../hooks/use-community-data";
import { useCohortSessions } from "../../hooks/use-schedule-data";
import { useMyCohortProgress, useMyEnrollment } from "../../hooks/use-student-record";
import { ClassroomChat } from "./classroom-chat";
import { ClassroomRoster } from "./classroom-roster";

/** Máximo de anuncios en la portada (el resto vive en /notifications). */
const ANNOUNCEMENT_LIMIT = 3;

export const StudentClassroomView: Component<{ cohortId: string }> = (props) => {
  const { t } = useI18n();
  const now = useNow();

  const cohortId = () => props.cohortId;
  const cohort = useCohort(cohortId);
  const courseId = () => cohort.data?.course_id ?? undefined;
  const { course } = useCourseTree(courseId);
  const enrollment = useMyEnrollment(cohortId);
  const teachers = useCohortTeacherProfiles(cohortId);
  const sessions = useCohortSessions(cohortId);
  const announcements = useAnnouncementsByCohort(cohortId);
  const progress = useMyCohortProgress(cohortId, courseId);
  // M5: el plan de clase sigue gateado por su feature (solo UX; la ruta también).
  const flipped = useFeatureEnabled("academy.flipped_sessions");
  // B2: compañeros y chat, cada uno con su interruptor. `chat` REQUIERE
  // `roster` en el grafo, así que apagar el roster apaga el chat de verdad
  // (también en el servidor) y no solo su panel.
  const rosterFlag = useFeatureEnabled("academy.classroom.roster");
  const chatFlag = useFeatureEnabled("academy.classroom.chat");

  /** La primera clase de hoy en adelante (la «próxima clase» de la cabecera). */
  const nextSession = createMemo(() => {
    const today = localDateKey(new Date());
    return (sessions.data ?? [])
      .filter((s) => s.deleted_at == null && (s.date ?? "") >= today)
      .toSorted(
        (a, b) =>
          (a.date ?? "").localeCompare(b.date ?? "") ||
          (a.start_time ?? "").localeCompare(b.start_time ?? ""),
      )
      .at(0);
  });

  const nextTimes = () => ({
    date: nextSession()?.date,
    startTime: nextSession()?.start_time,
    endTime: nextSession()?.end_time,
  });

  const pinnedFirst = createMemo(() =>
    (announcements.data ?? [])
      .toSorted(
        (a, b) =>
          (b.pinned ?? 0) - (a.pinned ?? 0) ||
          (b.created_at ?? "").localeCompare(a.created_at ?? ""),
      )
      .slice(0, ANNOUNCEMENT_LIMIT),
  );

  const planPct = () => {
    const { planDone, planTotal } = progress();
    return planTotal > 0 ? Math.round((planDone / planTotal) * 100) : 0;
  };

  return (
    <PageContainer maxWidth="3xl">
      <div class="space-y-5" data-testid="student-classroom">
        <Link to="/classroom" class="text-sm text-muted-foreground hover:text-foreground">
          ← {t(msg`My classrooms`)}
        </Link>

        <Show
          when={!cohort.isLoading || cohort.data}
          fallback={<Skeleton class="h-40 w-full rounded-2xl" />}
        >
          <Show
            when={enrollment.row()}
            fallback={
              <div data-testid="classroom-not-found">
                <NotFoundState
                  variant="no-access"
                  title={t(msg`Classroom not available`)}
                  description={t(
                    msg`You are not enrolled in this cohort, or it hasn't synced to this device yet.`,
                  )}
                />
              </div>
            }
          >
            {/* ── Cabecera: curso · cohorte · profesorado · próxima clase ── */}
            <header
              class="space-y-3 rounded-2xl border border-border p-5"
              data-testid="classroom-header"
            >
              <div class="flex items-start justify-between gap-2">
                <div class="min-w-0">
                  <p class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    {cohort.data?.name ?? cohort.data?.code ?? ""}
                  </p>
                  <h1
                    class="flex items-center gap-2 font-display text-2xl font-extrabold"
                    data-testid="classroom-course-title"
                  >
                    <School class="h-6 w-6 shrink-0 text-primary" />
                    {course.data?.title ?? t(msg`My classroom`)}
                  </h1>
                </div>
                <HelpButton area="classroom" />
              </div>

              <Show when={(teachers.data ?? []).length > 0}>
                <ul class="flex flex-wrap gap-2" data-testid="classroom-teachers">
                  <For each={teachers.data ?? []}>
                    {(teacher) => (
                      <li class="flex items-center gap-2 rounded-full bg-muted/50 py-1 pe-3 ps-1 text-xs">
                        <Show
                          when={teacher.photo}
                          fallback={
                            <span class="flex h-6 w-6 items-center justify-center rounded-full bg-accent/15 text-accent">
                              <BookOpen class="h-3 w-3" />
                            </span>
                          }
                        >
                          {(photo) => (
                            <img src={photo()} alt="" class="h-6 w-6 rounded-full object-cover" />
                          )}
                        </Show>
                        <span class="font-medium">{teacher.title ?? t(msg`Teacher`)}</span>
                      </li>
                    )}
                  </For>
                </ul>
              </Show>

              <Show
                when={nextSession()}
                fallback={
                  <p class="text-sm text-muted-foreground" data-testid="classroom-no-next-session">
                    {t(msg`No upcoming classes scheduled.`)}
                  </p>
                }
              >
                {(session) => (
                  <div
                    class="flex flex-wrap items-center gap-2 rounded-xl bg-primary/5 px-3 py-2 text-sm"
                    data-testid="classroom-next-session"
                  >
                    <CalendarClock class="h-4 w-4 text-primary" />
                    <span class="font-semibold">{t(msg`Next class`)}:</span>
                    <span>
                      {session().date} · {(session().start_time ?? "").slice(0, 5)}
                    </span>
                    <span class="text-muted-foreground">{session().topic ?? ""}</span>
                    <EnVivoBadge times={nextTimes()} />
                    <Show when={joinWindowState(now(), nextTimes()).isLiveNow}>
                      <Link
                        to={`/live/${session().id}`}
                        class="inline-flex items-center gap-1 rounded-lg bg-primary px-2.5 py-1 text-xs font-bold text-primary-foreground"
                        data-testid="classroom-enter-live"
                      >
                        <Video class="h-3.5 w-3.5" /> {t(msg`Enter`)}
                      </Link>
                    </Show>
                  </div>
                )}
              </Show>
            </header>

            {/* ── Avance: requisitos de certificación + plan de clase ── */}
            <section
              class="space-y-3 rounded-2xl border border-border p-5"
              data-testid="classroom-progress"
            >
              <h2 class="font-display text-lg font-bold">{t(msg`Your progress`)}</h2>
              <CompletionProgress cohortId={props.cohortId} />
              <Show when={progress().planTotal > 0}>
                <div>
                  <p
                    class="mb-1 text-xs text-muted-foreground"
                    data-testid="classroom-plan-progress"
                  >
                    {t(msg`Class plan`)}: {progress().planDone}/{progress().planTotal}
                  </p>
                  <div class="h-2.5 overflow-hidden rounded-full bg-muted/60">
                    <div
                      class="h-full rounded-full bg-gradient-to-r from-accent to-joy-berry transition-all duration-500"
                      style={{ width: `${planPct()}%` }}
                    />
                  </div>
                </div>
              </Show>
              <Link
                to="/profile/academic"
                class="inline-block text-sm font-semibold text-primary hover:underline"
                data-testid="classroom-open-record"
              >
                {t(msg`See my full record`)} →
              </Link>
            </section>

            {/* ── Anuncios de MI cohorte ── */}
            <section class="space-y-2 rounded-2xl border border-border p-5">
              <h2 class="flex items-center gap-2 font-display text-lg font-bold">
                <Megaphone class="h-5 w-5 text-accent" /> {t(msg`Announcements`)}
              </h2>
              <Show
                when={pinnedFirst().length > 0}
                fallback={
                  <p class="text-sm text-muted-foreground" data-testid="classroom-no-announcements">
                    {t(msg`No announcements for this cohort.`)}
                  </p>
                }
              >
                <ul class="space-y-2" data-testid="classroom-announcements">
                  <For each={pinnedFirst()}>
                    {(a) => (
                      <li
                        class="rounded-xl border border-border p-3"
                        data-testid="classroom-announcement"
                      >
                        <p class="flex items-center gap-1.5 text-sm font-semibold">
                          <Show when={a.pinned}>
                            <Pin class="h-3.5 w-3.5 text-accent" />
                          </Show>
                          {a.title}
                        </p>
                        <Show when={a.body}>
                          <p class="mt-0.5 whitespace-pre-line text-xs text-muted-foreground">
                            {a.body}
                          </p>
                        </Show>
                      </li>
                    )}
                  </For>
                </ul>
              </Show>
            </section>

            {/* ── B2 · Compañeros del aula (flag `…classroom.roster`) ── */}
            <Show when={rosterFlag().enabled}>
              <ClassroomRoster cohortId={props.cohortId} />
            </Show>

            {/* ── B2 · Chat de la cohorte (flag `…classroom.chat`) ──
                Apagarlo NO deja el aula muda: el foro del curso sigue ahí. */}
            <Show when={chatFlag().enabled}>
              <ClassroomChat
                cohortId={props.cohortId}
                organizationId={cohort.data?.organization_id}
                // El alumno nunca modera su propia aula. Quien enseña entra por
                // `/academy/cohorts`, no por aquí, así que esta vista pinta la
                // versión sin herramientas de moderación.
                canModerate={false}
                locked={Boolean(cohort.data?.chat_locked)}
              />
            </Show>

            {/* ── Clases: plan por sesión (la puerta al plan de clase) ── */}
            <section class="space-y-2 rounded-2xl border border-border p-5">
              <h2 class="flex items-center gap-2 font-display text-lg font-bold">
                <ListChecks class="h-5 w-5 text-accent" /> {t(msg`Classes`)}
              </h2>
              <Show
                when={progress().sessions.length > 0}
                fallback={
                  <p class="text-sm text-muted-foreground">{t(msg`No classes scheduled yet.`)}</p>
                }
              >
                <ul class="space-y-1.5" data-testid="classroom-sessions">
                  <For each={progress().sessions}>
                    {(row) => (
                      <li
                        class="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border px-3 py-2 text-sm"
                        data-testid="classroom-session-row"
                        data-attendance={row.attendance ?? ""}
                      >
                        <span class="min-w-0">
                          <span class="font-semibold">{row.date}</span>{" "}
                          <span class="text-muted-foreground">{row.topic ?? t(msg`Class`)}</span>
                        </span>
                        <span class="flex items-center gap-2 text-xs">
                          <Show when={row.planTotal > 0}>
                            <span class="text-muted-foreground">
                              {row.planDone}/{row.planTotal}
                            </span>
                          </Show>
                          <Show when={flipped().enabled}>
                            <Link
                              to={`/schedule/${row.sessionId}`}
                              class="rounded-lg border border-border px-2 py-1 font-semibold hover:bg-muted"
                              data-testid="classroom-open-plan"
                            >
                              {t(msg`Plan`)}
                            </Link>
                          </Show>
                        </span>
                      </li>
                    )}
                  </For>
                </ul>
              </Show>
            </section>

            {/* ── Puertas al contenido del curso ── */}
            <div class="flex flex-wrap gap-2">
              <Show when={courseId()}>
                {(cid) => (
                  <Link
                    to={`/learn/${cid()}`}
                    class="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm font-semibold hover:border-primary/50"
                    data-testid="classroom-open-modules"
                  >
                    <BookOpen class="h-4 w-4 text-primary" /> {t(msg`Course modules`)}
                  </Link>
                )}
              </Show>
              <Link
                to="/assignments"
                class="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm font-semibold hover:border-primary/50"
                data-testid="classroom-open-assignments"
              >
                <ClipboardList class="h-4 w-4 text-primary" /> {t(msg`My assignments`)}
              </Link>
            </div>

            {/* Grabación de la última clase impartida (si la hay). */}
            <Show when={(sessions.data ?? []).find((s) => s.recording_video_id)}>
              {(session) => (
                <div class="rounded-2xl border border-border p-4" data-testid="classroom-recording">
                  <p class="mb-2 text-sm font-semibold">{t(msg`Class recording`)}</p>
                  <RecordingPlayer
                    videoId={session().recording_video_id!}
                    title={session().topic ?? undefined}
                  />
                </div>
              )}
            </Show>
          </Show>
        </Show>
      </div>
    </PageContainer>
  );
};
